import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { INVOICE_LOGO_BASE64 } from '../invoiceLogoBase64';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const BRAND = {
  navyDark: '#081325',
  navy: '#0B2545',
  crimson: '#C1121F',
  crimsonLight: '#FDF2F2',
  slateDark: '#0F172A',
  slate: '#334155',
  slateLight: '#64748B',
  border: '#CBD5E1',
  borderLight: '#E2E8F0',
  bgLight: '#F8FAFC',
  bgCard: '#F1F5F9',
  white: '#FFFFFF',
  greenDark: '#166534',
  greenBg: '#DCFCE7',
  goldDark: '#854D0E',
  goldBg: '#FEF9C3',
};

const fmtMoney = (val) => {
  const n = Number(val || 0);
  return 'PKR ' + n.toLocaleString('en-US');
};

const formatDate = (dateStr) => {
  if (!dateStr) return new Date().toLocaleDateString('en-GB');
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleDateString('en-GB');
};

const S = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 24,
    paddingLeft: 28,
    paddingRight: 28,
    fontFamily: 'Helvetica',
    fontSize: 8.5,
    color: BRAND.slateDark,
    backgroundColor: BRAND.white,
  },

  /* ── Header ── */
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1.5,
    borderBottomColor: BRAND.navy,
    marginBottom: 12,
  },
  logoBox: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 100,
    height: 44,
    objectFit: 'contain',
  },
  companyMeta: {
    marginLeft: 12,
    borderLeftWidth: 1,
    borderLeftColor: BRAND.border,
    paddingLeft: 10,
  },
  companyName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
    color: BRAND.navy,
    letterSpacing: 0.5,
  },
  companySub: {
    fontSize: 7,
    color: BRAND.slateLight,
    marginTop: 2,
  },
  companyReg: {
    fontSize: 6.5,
    color: BRAND.slateLight,
    marginTop: 1,
  },

  /* Title Box */
  titleBox: {
    alignItems: 'flex-end',
  },
  titleBadge: {
    backgroundColor: BRAND.navy,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 3,
    marginBottom: 4,
  },
  titleBadgeText: {
    color: BRAND.white,
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    letterSpacing: 0.8,
  },
  docTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 13,
    color: BRAND.navy,
  },
  docPeriod: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: BRAND.crimson,
    marginTop: 2,
  },

  /* ── Metadata Strip ── */
  metaStrip: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: BRAND.bgLight,
    borderWidth: 1,
    borderColor: BRAND.borderLight,
    borderRadius: 4,
    padding: 8,
    marginBottom: 12,
  },
  metaItem: {
    flexDirection: 'column',
  },
  metaLabel: {
    fontSize: 6.5,
    color: BRAND.slateLight,
    textTransform: 'uppercase',
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 8.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.slateDark,
  },

  /* Status Badges */
  statusPaid: {
    backgroundColor: BRAND.greenBg,
    color: BRAND.greenDark,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    alignSelf: 'flex-start',
  },
  statusPending: {
    backgroundColor: BRAND.goldBg,
    color: BRAND.goldDark,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    alignSelf: 'flex-start',
  },

  /* ── Employee Details Card ── */
  empCard: {
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 4,
    marginBottom: 12,
    overflow: 'hidden',
  },
  empCardHeader: {
    backgroundColor: BRAND.navy,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  empCardHeaderText: {
    color: BRAND.white,
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  empCardBody: {
    flexDirection: 'row',
    padding: 8,
    backgroundColor: BRAND.white,
  },
  empCol: {
    flex: 1,
    paddingRight: 8,
  },
  empRow: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  empKey: {
    width: 90,
    fontSize: 7.5,
    color: BRAND.slateLight,
    fontFamily: 'Helvetica-Bold',
  },
  empVal: {
    flex: 1,
    fontSize: 8,
    color: BRAND.slateDark,
    fontFamily: 'Helvetica',
  },

  /* ── Financial Tables (Side-by-Side) ── */
  financeGrid: {
    flexDirection: 'row',
    marginBottom: 12,
    gap: 8,
  },
  tableBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: BRAND.bgCard,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tableHeaderTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: BRAND.navy,
    textTransform: 'uppercase',
  },
  tableHeaderCur: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
    color: BRAND.slateLight,
  },
  tableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND.borderLight,
  },
  tableRowAlt: {
    backgroundColor: BRAND.bgLight,
  },
  tableLabel: {
    fontSize: 8,
    color: BRAND.slateDark,
  },
  tableSubLabel: {
    fontSize: 6.5,
    color: BRAND.slateLight,
    marginTop: 1,
  },
  tableNum: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.slateDark,
  },
  tableNumDeduct: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.crimson,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: BRAND.bgCard,
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
  },
  totalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: BRAND.navy,
  },
  totalNum: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: BRAND.navy,
  },
  totalNumDeduct: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: BRAND.crimson,
  },

  /* ── Net Payable Banner ── */
  netPayBanner: {
    backgroundColor: BRAND.navyDark,
    borderRadius: 4,
    padding: 10,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: BRAND.navy,
  },
  netPayLeft: {
    flexDirection: 'column',
  },
  netPayKicker: {
    color: '#94A3B8',
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  netPayTitle: {
    color: BRAND.white,
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    marginTop: 2,
  },
  netPayRight: {
    alignItems: 'flex-end',
  },
  netPayAmount: {
    color: '#38BDF8',
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    letterSpacing: 0.5,
  },
  netPayCurrencyBadge: {
    color: '#94A3B8',
    fontSize: 7,
    marginTop: 2,
  },

  /* ── Notes / Contribution Box ── */
  notesBox: {
    backgroundColor: BRAND.bgLight,
    borderWidth: 1,
    borderColor: BRAND.borderLight,
    borderRadius: 4,
    padding: 8,
    marginBottom: 14,
  },
  notesLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
    color: BRAND.navy,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  notesContent: {
    fontSize: 7.5,
    color: BRAND.slate,
    lineHeight: 1.3,
  },

  /* ── Signatures & Authorization ── */
  sigGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 8,
    paddingTop: 10,
  },
  sigBlock: {
    width: '42%',
    alignItems: 'center',
  },
  sigLine: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
    marginBottom: 4,
    height: 24,
  },
  sigTitle: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.slateDark,
  },
  sigSub: {
    fontSize: 6.5,
    color: BRAND.slateLight,
    marginTop: 1,
  },

  /* ── Footer / Watermark ── */
  footer: {
    position: 'absolute',
    bottom: 14,
    left: 28,
    right: 28,
    borderTopWidth: 0.5,
    borderTopColor: BRAND.borderLight,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 6,
    color: BRAND.slateLight,
  },
  securityBadge: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.navy,
    letterSpacing: 0.4,
  },
});

export default function SalarySlipPDF({ data = {} }) {
  const monthNum = Number(data.month || new Date().getMonth() + 1);
  const monthStr = MONTH_NAMES[monthNum - 1] || 'Current Month';
  const yearStr  = String(data.year || new Date().getFullYear());
  const periodStr = `${monthStr.toUpperCase()} ${yearStr}`;

  const empName = data.employee?.name || data.employeeName || 'Staff Member';
  const empRole = data.employee?.role || data.employeeRole || 'Agent';
  const empEmail = data.employee?.email || 'N/A';
  const empIdStr = data.employeeId || data.employee?.id ? `PAJ-EMP-${String(data.employeeId || data.employee?.id).padStart(3, '0')}` : 'PAJ-EMP-000';

  const fixedSal = Number(data.fixedSalary || 0);
  const commAmt  = Number(data.commission || 0);
  const bonusAmt = Number(data.bonuses || 0);
  const grossPay = fixedSal + commAmt + bonusAmt;

  const deductAmt = Number(data.deductions || 0);
  const netPay    = grossPay - deductAmt;

  const carsCount  = Number(data.carsSold || 0);
  const profitGen  = Number(data.profitGenerated || 0);
  const isPaid     = (data.paymentStatus || '').toLowerCase() === 'paid';
  const paidAtDate = data.paidAt ? formatDate(data.paidAt) : null;
  const slipId     = `PAJ-PS-${yearStr}${String(monthNum).padStart(2, '0')}-${String(data.employeeId || data.employee?.id || 1).padStart(3, '0')}`;

  return (
    <Document title={`SalarySlip_${empName.replace(/\s+/g, '_')}_${monthStr}_${yearStr}`}>
      <Page size="A4" style={S.page}>
        {/* ── Official Letterhead ── */}
        <View style={S.headerContainer}>
          <View style={S.logoBox}>
            <Image src={INVOICE_LOGO_BASE64} style={S.logo} />
            <View style={S.companyMeta}>
              <Text style={S.companyName}>PLATINUM AUTO JAPAN CO., LTD.</Text>
              <Text style={S.companySub}>Global Automotive Export &amp; Auction Procurement Operations</Text>
              <Text style={S.companyReg}>Tokyo &amp; Yokohama Head Office · Corporate Tax ID: PAJ-JP-8829104-FIN</Text>
            </View>
          </View>

          <View style={S.titleBox}>
            <View style={S.titleBadge}>
              <Text style={S.titleBadgeText}>CONFIDENTIAL PAYROLL STATEMENT</Text>
            </View>
            <Text style={S.docTitle}>SALARY PAY SLIP</Text>
            <Text style={S.docPeriod}>{periodStr}</Text>
          </View>
        </View>

        {/* ── Top Metadata Strip ── */}
        <View style={S.metaStrip}>
          <View style={S.metaItem}>
            <Text style={S.metaLabel}>Statement Reference</Text>
            <Text style={S.metaValue}>{slipId}</Text>
          </View>
          <View style={S.metaItem}>
            <Text style={S.metaLabel}>Pay Period</Text>
            <Text style={S.metaValue}>{monthStr} {yearStr}</Text>
          </View>
          <View style={S.metaItem}>
            <Text style={S.metaLabel}>Issue Date</Text>
            <Text style={S.metaValue}>{formatDate(new Date())}</Text>
          </View>
          <View style={S.metaItem}>
            <Text style={S.metaLabel}>Remittance Status</Text>
            {isPaid ? (
              <Text style={S.statusPaid}>PAID {paidAtDate ? `(${paidAtDate})` : ''}</Text>
            ) : (
              <Text style={S.statusPending}>PENDING DISBURSEMENT</Text>
            )}
          </View>
        </View>

        {/* ── Employee Identification Profile ── */}
        <View style={S.empCard}>
          <View style={S.empCardHeader}>
            <Text style={S.empCardHeaderText}>Employee Identity &amp; Departmental Allocation</Text>
          </View>
          <View style={S.empCardBody}>
            <View style={S.empCol}>
              <View style={S.empRow}>
                <Text style={S.empKey}>Staff Full Name:</Text>
                <Text style={S.empVal}>{empName}</Text>
              </View>
              <View style={S.empRow}>
                <Text style={S.empKey}>Employee ID Code:</Text>
                <Text style={S.empVal}>{empIdStr}</Text>
              </View>
              <View style={S.empRow}>
                <Text style={S.empKey}>Assigned Email:</Text>
                <Text style={S.empVal}>{empEmail}</Text>
              </View>
            </View>
            <View style={S.empCol}>
              <View style={S.empRow}>
                <Text style={S.empKey}>Designation / Role:</Text>
                <Text style={S.empVal}>{empRole}</Text>
              </View>
              <View style={S.empRow}>
                <Text style={S.empKey}>Functional Division:</Text>
                <Text style={S.empVal}>{empRole === 'Manager' ? 'Sales Management' : 'Global Vehicle Sales'}</Text>
              </View>
              <View style={S.empRow}>
                <Text style={S.empKey}>Remuneration Unit:</Text>
                <Text style={S.empVal}>Pakistani Rupee (PKR)</Text>
              </View>
            </View>
          </View>
        </View>

        {/* ── Compensation Breakdown (Earnings vs Deductions) ── */}
        <View style={S.financeGrid}>
          {/* Earnings Column */}
          <View style={S.tableBox}>
            <View style={S.tableHeader}>
              <Text style={S.tableHeaderTitle}>Earnings &amp; Allowances</Text>
              <Text style={S.tableHeaderCur}>Amount (PKR)</Text>
            </View>

            <View style={S.tableRow}>
              <View>
                <Text style={S.tableLabel}>Basic Fixed Monthly Salary</Text>
                <Text style={S.tableSubLabel}>Agreed monthly base retainer</Text>
              </View>
              <Text style={S.tableNum}>{fmtMoney(fixedSal)}</Text>
            </View>

            <View style={[S.tableRow, S.tableRowAlt]}>
              <View>
                <Text style={S.tableLabel}>Deal &amp; Vehicle Sales Commission</Text>
                <Text style={S.tableSubLabel}>{carsCount} verified vehicle unit(s) closed</Text>
              </View>
              <Text style={S.tableNum}>{fmtMoney(commAmt)}</Text>
            </View>

            <View style={S.tableRow}>
              <View>
                <Text style={S.tableLabel}>Performance Bonuses &amp; Incentives</Text>
                <Text style={S.tableSubLabel}>Monthly milestone achievement reward</Text>
              </View>
              <Text style={S.tableNum}>{fmtMoney(bonusAmt)}</Text>
            </View>

            <View style={S.totalRow}>
              <Text style={S.totalLabel}>Gross Total Earnings</Text>
              <Text style={S.totalNum}>{fmtMoney(grossPay)}</Text>
            </View>
          </View>

          {/* Deductions Column */}
          <View style={S.tableBox}>
            <View style={S.tableHeader}>
              <Text style={S.tableHeaderTitle}>Deductions &amp; Adjustments</Text>
              <Text style={S.tableHeaderCur}>Amount (PKR)</Text>
            </View>

            <View style={S.tableRow}>
              <View>
                <Text style={S.tableLabel}>Advance Salary Recovery</Text>
                <Text style={S.tableSubLabel}>Prior drawdowns or disbursements</Text>
              </View>
              <Text style={S.tableNumDeduct}>{deductAmt > 0 ? `-${fmtMoney(deductAmt)}` : 'PKR 0'}</Text>
            </View>

            <View style={[S.tableRow, S.tableRowAlt]}>
              <View>
                <Text style={S.tableLabel}>Statutory &amp; Tax Withholdings</Text>
                <Text style={S.tableSubLabel}>Applicable regional levies</Text>
              </View>
              <Text style={S.tableNum}>PKR 0</Text>
            </View>

            <View style={S.tableRow}>
              <View>
                <Text style={S.tableLabel}>Absence &amp; Unexcused Deductions</Text>
                <Text style={S.tableSubLabel}>Attendance penalties</Text>
              </View>
              <Text style={S.tableNum}>PKR 0</Text>
            </View>

            <View style={S.totalRow}>
              <Text style={S.totalLabel}>Total Deductions</Text>
              <Text style={S.totalNumDeduct}>{deductAmt > 0 ? `-${fmtMoney(deductAmt)}` : 'PKR 0'}</Text>
            </View>
          </View>
        </View>

        {/* ── Net Payable Remuneration Callout ── */}
        <View style={S.netPayBanner}>
          <View style={S.netPayLeft}>
            <Text style={S.netPayKicker}>Net Take-Home Remuneration · 差引支給額</Text>
            <Text style={S.netPayTitle}>TOTAL NET PAYABLE SALARY</Text>
          </View>
          <View style={S.netPayRight}>
            <Text style={S.netPayAmount}>{fmtMoney(netPay)}</Text>
            <Text style={S.netPayCurrencyBadge}>Currency: Pakistani Rupee · Cleared Transfer</Text>
          </View>
        </View>

        {/* ── Performance Contribution Memo ── */}
        <View style={S.notesBox}>
          <Text style={S.notesLabel}>Performance Metrics &amp; Operational Notes</Text>
          <Text style={S.notesContent}>
            Closed vehicle sales during {monthStr} {yearStr}: {carsCount} unit(s). Gross enterprise sales volume / profit generated: {fmtMoney(profitGen)}.
            {data.notes ? `\nAdministrative Memo: ${data.notes}` : ''}
          </Text>
        </View>

        {/* ── Signatures & Authorization ── */}
        <View style={S.sigGrid}>
          <View style={S.sigBlock}>
            <View style={S.sigLine} />
            <Text style={S.sigTitle}>{empName}</Text>
            <Text style={S.sigSub}>Employee Signature &amp; Date</Text>
          </View>

          <View style={S.sigBlock}>
            <View style={S.sigLine} />
            <Text style={S.sigTitle}>Director of Finance &amp; Operations</Text>
            <Text style={S.sigSub}>Platinum Auto Japan Co., Ltd. Authorization</Text>
          </View>
        </View>

        {/* ── Page Footer ── */}
        <View style={S.footer}>
          <Text style={S.footerText}>
            Generated via Platinum CRM · Official Corporate Payroll Module · Highly Confidential
          </Text>
          <Text style={S.securityBadge}>
            VERIFIED &amp; COMPLIANT
          </Text>
        </View>
      </Page>
    </Document>
  );
}
